export interface Restaurant {
  name: string;
  emoji: string;
  // Extra words search should find this place by (cuisine, dish, occasion).
  tags?: string[];
  description: string;
  tip?: string;
  address: string;
  website: string;
  imageClass: string;
  backgroundImage: string;
  coordinates?: [number, number];
}

export interface RestaurantCategory {
  title: string;
  emoji: string;
  restaurants: Restaurant[];
}

export const restaurantCategories: RestaurantCategory[] = [
  {
    title: "Sit down dinner",
    emoji: "🕯️",
    restaurants: [
      {
        name: "Jeffry's",
        tags: ['american', 'date night', 'local', 'intimate'],
        emoji: "🥂",
        description: "A cozy neighborhood spot we love for a relaxed date night: thoughtful American cooking with local ingredients in a small, intimate room.",
        address: "1060 E Union St, Seattle, WA 98122",
        website: "https://www.jeffrysseattle.com",
        imageClass: "jeffrys",
        backgroundImage: "images/dining-jeffrys.webp",
        coordinates: [-122.3183672, 47.6130754],
      },
      {
        name: "Spinasse",
        tags: ['italian', 'pasta', 'date night', 'candlelit'],
        emoji: "🍝",
        description: "One of our favorite dinners in Seattle, right on 14th Ave. The handmade Piedmontese pasta is the draw — the tajarin with butter and sage is legendary — and the cozy, candlelit room is perfect for a special night.",
        address: "1531 14th Ave, Seattle, WA 98122",
        website: "https://spinasse.com/",
        imageClass: "spinasse",
        backgroundImage: "images/dining-spinasse.jpg",
        coordinates: [-122.3143994, 47.6150591],
      },
      {
        name: "Lark",
        tags: ['northwest', 'seasonal', 'celebration', 'special occasion', 'date night'],
        emoji: "🌿",
        description: "Where we go to celebrate. Lark has been a Seattle favorite for years thanks to beautiful seasonal plates from Northwest farms, served in a warm, candlelit room made for sharing.",
        address: "952 E Seneca St, Seattle, WA 98122",
        website: "https://www.larkseattle.com",
        imageClass: "lark",
        backgroundImage: "images/dining-lark.webp",
        coordinates: [-122.3191, 47.6110],
      },
      {
        name: "Bar Cotto",
        tags: ['pizza', 'italian', 'wine', 'deal', 'casual'],
        emoji: "🍷",
        description: "Our go-to for an easy, fun dinner nearby: wood-fired pizza, Italian cured meats, and natural wine in a lively little bar. 🎉 Deal: $70 3-Course Dinner for 2!",
        address: "1546 15th Ave, Seattle, WA 98122",
        website: "https://www.barcottopizzeria.com",
        imageClass: "bar-cotto",
        backgroundImage: "images/dining-bar-cotto.webp",
        coordinates: [-122.3125981, 47.6150507],
      },
      {
        name: "Messina Modern Italian Kitchen",
        tags: ['italian', 'pasta', 'queen anne'],
        emoji: "🍝",
        description: "Worth the trip to Queen Anne for the pasta flight — a sampler of several handmade pastas, so you don't have to choose. Seasonal Italian cooking with Pacific Northwest ingredients.",
        address: "1432 Queen Anne Ave N, Seattle, WA 98109",
        website: "https://www.messinamodernitalian.com",
        imageClass: "messina",
        backgroundImage: "images/dining-messina-pasta-flight.jpg",
        coordinates: [-122.3571806, 47.6308125],
      }
    ]
  },
  {
    title: "Brunch Food & Drink",
    emoji: "🍳",
    restaurants: [
      {
        name: "Oddfellows Café + Bar",
        tags: ['brunch', 'coffee', 'cocktails', 'american'],
        emoji: "☕",
        description: "The Pike/Pine corridor's living room. We love it for a slow brunch or an evening drink in its big, airy, vintage-feeling space, with good coffee, cocktails, and New American food all day.",
        address: "1525 10th Ave, Seattle, WA 98122",
        website: "https://www.oddfellowscafe.com",
        imageClass: "oddfellows",
        backgroundImage: "images/dining-pink-door.webp",
        coordinates: [-122.3196500, 47.6149543],
      },
      {
        name: "Terra Plata",
        tags: ['rooftop', 'patio', 'outdoor seating', 'farm to table', 'brunch'],
        emoji: "🍽️",
        description: "Book this when the sun's out. Farm-to-table cooking and a rooftop patio make it one of our favorite spots for brunch or dinner under the open sky.",
        address: "1501 Melrose Ave, Seattle, WA 98122",
        website: "https://www.terraplata.com",
        imageClass: "terra-plata",
        backgroundImage: "images/dining-terra-plata-rooftop.webp",
        coordinates: [-122.3280798, 47.6143873],
      },
      {
        name: "Glo's",
        tags: ['eggs benedict', 'pancakes', 'breakfast', 'comfort food'],
        emoji: "🍳",
        description: "Our favorite neighborhood brunch. Generous comfort food — creative eggs Benedicts, fluffy pancakes — and a friendly crew that makes the wait worth it.",
        tip: "If there's a wait, head to Smith just down the street.",
        address: "1621 E Olive Way, Seattle, WA 98102",
        website: "https://www.glosseattle.com",
        imageClass: "glos",
        backgroundImage: "https://images.squarespace-cdn.com/content/v1/6436df4964f2556eff7e8d03/1689873789938-2X6SVFU37CVV4AGTZNT5/20230514_METIS_GLOS_+97.jpg?format=2500w",
        coordinates: [-122.3244952, 47.6193455],
      },
      {
        name: "Smith",
        tags: ['whiskey', 'brunch', 'bar', 'cozy'],
        emoji: "🥂",
        description: "A great all-day hangout on 15th Ave: dim and cozy, with a long whiskey list and a brunch that's always a good time.",
        tip: "Great fallback if Glo's is full.",
        address: "332 15th Ave E, Seattle, WA 98112",
        website: "https://www.smithseattle.com",
        imageClass: "smith",
        backgroundImage: "images/dining-smith.webp",
        coordinates: [-122.3128, 47.6225],
      }
    ]
  },
  {
    title: "Speakeasies & Cocktails",
    emoji: "🍸",
    restaurants: [
      {
        name: "Canon",
        tags: ['whiskey', 'cocktails', 'date night', 'world class'],
        emoji: "🍸",
        description: "One of the best cocktail bars in the country, right in the neighborhood. The vintage apothecary room holds over 4,000 bottles, and the drinks are as good as the setting.",
        address: "928 12th Ave, Seattle, WA 98122",
        website: "https://www.canonseattle.com",
        imageClass: "canon",
        backgroundImage: "images/dining-canon-bar.webp",
        coordinates: [-122.3164758, 47.6112731],
      },
      {
        name: "Tavern Law",
        tags: ['speakeasy', 'hidden', 'cocktails', 'date night'],
        emoji: "🍸",
        description: "Our pick for a special night out. Downstairs is a classic cocktail bar; pick up the house phone by the vault-style door to ask for a seat in the hidden speakeasy upstairs.",
        address: "1406 12th Ave, Seattle, WA 98122",
        website: "https://www.tavernlaw.com",
        imageClass: "tavern-law",
        backgroundImage: "images/dining-tavern-law-bar.webp",
        coordinates: [-122.3166280, 47.6132837],
      },
      {
        name: "Taylor Shellfish Farms",
        tags: ['oysters', 'seafood', 'shellfish', 'happy hour'],
        emoji: "🦪",
        description: "The best place to try Washington oysters. Taylor farms its own shellfish in Puget Sound, so everything at the bar is about as fresh as it gets.",
        address: "1521 Melrose Ave, Seattle, WA 98122",
        website: "https://www.taylorshellfish.com",
        imageClass: "taylor-shellfish",
        backgroundImage: "images/dining-taylor-shellfish.webp",
        coordinates: [-122.3280607, 47.6146314],
      },
      {
        name: "Death & Co",
        tags: ['cocktails', 'pioneer square', 'date night', 'moody'],
        emoji: "🥃",
        description: "Worth the trip to Pioneer Square: the legendary New York cocktail bar's Seattle outpost, tucked into historic RailSpur Alley. Expect a moody, low-lit room, a focused menu of beautifully made drinks, and Northwest-leaning small plates.",
        tip: "Reserve a table on their site, especially on weekends.",
        address: "419 Occidental Ave S, Seattle, WA 98104",
        website: "https://www.deathandcompany.com/location/seattle/",
        imageClass: "death-and-co",
        backgroundImage: "images/dining-death-and-co.webp",
        coordinates: [-122.3326, 47.5985],
      }
    ]
  },
  {
    title: "Asian Eats",
    emoji: "🍜",
    restaurants: [
      {
        name: "Bai Tong",
        tags: ['thai', 'pad thai', 'curry', 'groups'],
        emoji: "🇹🇭",
        description: "Our pick for lively, easy Thai near the house. The crispy garlic chicken is a must, plus pad Thai, curries, and plenty of small plates to share.",
        address: "1121 E Pike St, Seattle, WA 98122",
        website: "https://baitongthaistreet.com/",
        imageClass: "bai-tong",
        backgroundImage: "images/dining-bai-tong.webp",
        coordinates: [-122.3171112, 47.6139854],
      },
      {
        name: "Momiji",
        tags: ['sushi', 'japanese', 'sashimi', 'garden', 'date night'],
        emoji: "🍣",
        description: "Great sushi in a beautiful setting: fresh sashimi and creative rolls (try the soft-shell crab or the Ultimate Dragon) beside a calm Japanese garden courtyard.",
        address: "1522 12th Ave, Seattle, WA 98122",
        website: "https://www.momijiseattle.com",
        imageClass: "momiji",
        backgroundImage: "images/dining-momiji-courtyard.webp",
        coordinates: [-122.3165268, 47.6148043],
      },
      {
        name: "Kedai Makan",
        tags: ['malaysian', 'laksa', 'nasi lemak', 'street food', 'spicy'],
        emoji: "🍛",
        description: "Bold, delicious Malaysian street food — go for the nasi lemak or a bowl of laksa. It's small and busy, and always worth it.",
        address: "1449 E Pine St, Seattle, WA 98122",
        website: "https://www.kedaimakan.com",
        imageClass: "kedai-makan",
        backgroundImage: "images/dining-kedai-makan.webp",
        coordinates: [-122.3129850, 47.6151546],
      },
      {
        name: "Ramen Danbo",
        tags: ['ramen', 'japanese', 'noodles', 'tonkotsu'],
        emoji: "🍜",
        description: "Our favorite ramen in town. Rich Fukuoka-style tonkotsu where you pick your noodle firmness and spice level — perfect after a day of exploring.",
        tip: "Go during lunch hours to avoid wait time.",
        address: "1222 E Pine St, Suite A, Seattle, WA 98122",
        website: "https://www.ramendanbo-usa.com",
        imageClass: "ramen-danbo",
        backgroundImage: "images/dining-ramen-danbo.webp",
        coordinates: [-122.3157792, 47.6154249],
      },
      {
        name: "Pho Bac Súp Shop",
        tags: ['pho', 'vietnamese', 'noodles', 'soup'],
        emoji: "🍜",
        description: "Seattle's pho story starts with this family, who opened one of the city's first pho shops nearby. Their modern Súp Shop serves rich pho and other Vietnamese soups in a bright, fresh space.",
        address: "1240 S Jackson St, Seattle, WA 98144",
        website: "https://www.phobacsupshop.com",
        imageClass: "pho-bac-sup-shop",
        backgroundImage: "images/dining-pho-bac-sup-shop.jpg",
        coordinates: [-122.3153827, 47.5994793],
      },
      {
        name: "Busan Jeong",
        tags: ['korean', 'soup', 'dumplings', 'kimchi', 'comfort food'],
        emoji: "🍲",
        description: "Real Korean comfort food. The specialty is Busan-style dwaeji-gukbap — rich, slow-simmered pork-bone soup with rice or noodles — plus house dumplings and kimchi. Perfect on a rainy Seattle night.",
        address: "618 Broadway E, Seattle, WA 98102",
        website: "https://www.google.com/maps/search/?api=1&query=Busan+Jeong%2C+618+Broadway+E%2C+Seattle%2C+WA+98102",
        imageClass: "busan-jeong",
        backgroundImage: "images/dining-busan-jeong.jpg",
        coordinates: [-122.3206979, 47.6249235],
      },
      {
        name: "Bangrak Market",
        tags: ['thai', 'street food', 'belltown'],
        emoji: "🇹🇭",
        description: "Fun, casual Thai street food in Belltown with big flavors and generous portions — a great stop before or after a day downtown.",
        address: "2319 2nd Ave, Seattle, WA 98121",
        website: "https://www.bangrakmarket.com",
        imageClass: "bangrak-market",
        backgroundImage: "images/dining-bangrak-market.jpeg",
        coordinates: [-122.3465048, 47.6143274],
      }
    ]
  },
  {
    title: "Fast Eats",
    emoji: "🌮",
    restaurants: [
      {
        name: "Katsu Burger",
        tags: ['burger', 'fries', 'milkshake', 'japanese', 'quick bite'],
        emoji: "🍔",
        description: "A Seattle original we love: crunchy panko-fried katsu burgers, nori-dusted fries, and fun milkshakes.",
        address: "1410 12th Ave, Seattle, WA 98122",
        website: "https://www.katsuburger.com",
        imageClass: "katsu-burger",
        backgroundImage: "images/dining-katsu-burger.webp",
        coordinates: [-122.3166170, 47.6134133],
      },
      {
        name: "Dave's Hot Chicken",
        tags: ['chicken', 'spicy', 'sliders', 'quick bite', 'late night'],
        emoji: "🔥",
        description: "When you want something fast and fiery: Nashville-style hot chicken tenders and sliders, with heat from \"No Spice\" all the way to \"Reaper.\"",
        address: "1200 E Pike St, Seattle, WA 98122",
        website: "https://www.daveshotchicken.com",
        imageClass: "daves-hot-chicken",
        backgroundImage: "images/dining-daves-hot-chicken.webp",
        coordinates: [-122.3164489, 47.6143129],
      },
      {
        name: "Carmelo's Tacos",
        tags: ['tacos', 'mexican', 'takeout', 'quick bite'],
        emoji: "🌮",
        description: "Our favorite quick tacos. A family-owned taqueria with Mexico City-style street tacos on homemade tortillas — and they take online orders!",
        address: "1223 E Cherry St, Space C121A, Seattle, WA 98122",
        website: "https://www.carmelostacos.com",
        imageClass: "carmelos-tacos",
        backgroundImage: "images/dining-carmelos-tacos.webp",
        coordinates: [-122.3165071, 47.6077343],
      },
      {
        name: "Café Hagen Modern European Cafe",
        tags: ['coffee', 'sandwiches', 'lunch', 'pastries'],
        emoji: "🥐",
        description: "A calm, stylish stop in South Lake Union for European-style coffee, sandwiches, and pastries — ideal for a quick lunch.",
        address: "1252 Thomas St, Seattle, WA 98109",
        website: "https://www.cafehagenseattle.com",
        imageClass: "cafe-hagen",
        backgroundImage: "images/dining-cafe-hagen.webp",
        coordinates: [-122.3310375, 47.6208991],
      }
    ]
  },
  {
    title: "Coffee & Cafés",
    emoji: "☕",
    restaurants: [
      {
        name: "Anchorhead Coffee",
        tags: ['latte', 'quaffle', 'waffle', 'breakfast'],
        emoji: "☕",
        description: "Our pick for a fun coffee stop: creative lattes, house-roasted beans, and the \"quaffle\" — a waffle made from croissant dough that you shouldn't skip.",
        address: "115 12th Ave, Seattle, WA 98122",
        website: "https://www.anchorheadcoffee.com",
        imageClass: "anchorhead-coffee",
        backgroundImage: "images/dining-anchorhead-coffee.webp",
        coordinates: [-122.3169930, 47.6120238],
      },
      {
        name: "Caffe Vita",
        tags: ['roaster', 'espresso', 'classic'],
        emoji: "☕",
        description: "A true Seattle classic since 1995. Vita roasts its coffee right here on Pike Street, and the café has deep roots in the city's music scene.",
        address: "1005 E Pike St, Seattle, WA 98122",
        website: "https://www.caffevita.com",
        imageClass: "caffe-vita",
        backgroundImage: "images/dining-caffe-vita.webp",
        coordinates: [-122.3190513, 47.6138670],
      },
      {
        name: "Espresso Vivace",
        tags: ['espresso', 'latte art', 'classic'],
        emoji: "☕",
        description: "A Seattle espresso legend on Broadway. Vivace helped pioneer latte art, and the shots are still some of the best in town — try the Caffe Nico.",
        address: "532 Broadway E, Seattle, WA 98102",
        website: "https://www.espressovivace.com",
        imageClass: "espresso-vivace",
        backgroundImage: "images/dining-espresso-vivace.webp",
        coordinates: [-122.3207443, 47.6238007],
      },
      {
        name: "Olympia Coffee Roasting",
        tags: ['espresso', 'pour-over', 'roaster', 'downtown'],
        emoji: "☕",
        description: "One of the Northwest's most respected roasters. Their downtown café is a great stop for a carefully made espresso or pour-over between sightseeing.",
        address: "1420 5th Ave, Suite 103, Seattle, WA 98101",
        website: "https://www.olympiacoffee.com/pages/downtown-seattle",
        imageClass: "olympia-coffee",
        backgroundImage: "images/dining-olympia-coffee.webp",
        coordinates: [-122.3346647, 47.6105449],
      },
      {
        name: "Storyville Coffee",
        tags: ['pike place', 'pastries', 'views', 'quiet'],
        emoji: "☕",
        description: "Our favorite coffee break at Pike Place. Tucked upstairs in the Corner Market Building, it's a calm, cozy escape from the crowds, with house-made pastries and views over the market and Elliott Bay.",
        address: "94 Pike St #34, Seattle, WA 98101",
        website: "https://www.storyville.com",
        imageClass: "storyville-coffee",
        backgroundImage: "images/dining-storyville-coffee.webp",
        coordinates: [-122.3405657, 47.6088560],
      }
    ]
  },
  {
    title: "Sweets & Treats",
    emoji: "🥐",
    restaurants: [
      {
        name: "Cupcake Royale",
        tags: ['cupcakes', 'dessert', 'espresso'],
        emoji: "🧁",
        description: "A Seattle original for a sweet break: creative cupcakes, good espresso, and a cozy neighborhood feel.",
        address: "1102 East Pike Street, Seattle, WA",
        website: "https://www.cupcakeroyale.com",
        imageClass: "cupcake-royale",
        backgroundImage: "images/dining-cupcake-royale.jpg",
        coordinates: [-122.3177809, 47.6142404],
      },
      {
        name: "Molly Moon's",
        tags: ['ice cream', 'dessert', 'kid-friendly', 'summer'],
        emoji: "🍦",
        description: "Seattle's favorite ice cream. Classics like Honey Lavender and Salted Caramel are made with local dairy, and the line on a summer night is part of the fun.",
        address: "917 E Pine St, Seattle, WA 98122",
        website: "https://www.mollymoon.com",
        imageClass: "molly-moons",
        backgroundImage: "images/dining-molly-moons.jpg",
        coordinates: [-122.3198203, 47.6151601],
      },
      {
        name: "Bakery Nouveau",
        tags: ['bakery', 'pastries', 'croissant', 'chocolate', 'tiramisu', 'cake'],
        emoji: "🥐",
        description: "One of Seattle's best bakeries, right in the neighborhood: flaky Parisian-style pastries, rich chocolates, and great bread. We still talk about the tiramisu.",
        address: "1435 E John St, Seattle, WA 98112",
        website: "https://www.bakerynouveau.com",
        imageClass: "bakery-nouveau",
        backgroundImage: "images/dining-bakery-nouveau.webp",
        coordinates: [-122.3130841, 47.6194987],
      },
      {
        name: "Voodoo Doughnut",
        tags: ['donuts', 'doughnuts', 'dessert', 'kid-friendly'],
        emoji: "🍩",
        description: "A fun, over-the-top treat from the famous Portland doughnut shop — wild flavors and toppings you won't find anywhere else.",
        address: "1201 Pine St, Seattle, WA 98101",
        website: "https://www.voodoodoughnut.com",
        imageClass: "voodoo-doughnut",
        backgroundImage: "images/dining-voodoo-doughnut.webp",
        coordinates: [-122.3285088, 47.6148431],
      },
      {
        name: "Dochi",
        tags: ['mochi', 'donuts', 'doughnuts', 'japanese', 'snack'],
        emoji: "🍩",
        description: "A fun snack stop in the Chinatown-International District: chewy Japanese-style mochi doughnuts with rotating glazes, inside the Uwajimaya food hall — browse the market while you're there.",
        address: "515 S Weller St, Seattle, WA 98104",
        website: "https://www.dochicompany.com/order-online",
        imageClass: "dochi",
        backgroundImage: "images/dining-dochi.jpg",
        coordinates: [-122.325779, 47.597948],
      },
      {
        name: "Temple Pastries",
        tags: ['croissant', 'cruffin', 'sourdough', 'pastries', 'breakfast'],
        emoji: "🥐",
        description: "Worth the trip to the Central District for some of Seattle's most inventive pastries: flaky sourdough croissants, cruffins, and seasonal flavors almost too pretty to eat. Pair one with a coffee and you've got the perfect morning.",
        tip: "Open daily 7 AM–3 PM; check their site before you go.",
        address: "2524 S Jackson St, Seattle, WA 98144",
        website: "https://templepastries.com",
        imageClass: "temple-pastries",
        backgroundImage: "images/dining-temple-pastries.webp",
        coordinates: [-122.2988, 47.5994],
      },
      {
        name: "Sea Wolf Bakers",
        tags: ['bakery', 'sourdough', 'bread', 'croissant', 'breakfast'],
        emoji: "🥖",
        description: "One of our favorite bakeries in the city. The naturally leavened sourdough and buttery croissants are worth the trip to Fremont — perfect with a morning coffee.",
        address: "3617 Stone Way N Ste 101, Seattle, WA 98103",
        website: "https://www.seawolfbakers.com",
        imageClass: "sea-wolf-bakers",
        backgroundImage: "images/dining-sea-wolf-bakers.jpg",
        coordinates: [-122.3432540, 47.6509186],
      },
      {
        name: "Deep Sea Sugar And Salt",
        tags: ['dessert', 'bubble tea', 'boba', 'shaved ice', 'cake'],
        emoji: "🧋",
        description: "Worth a trip to Georgetown for its playful dessert café: creative bubble teas, shaved ice, and treats as fun to look at as they are to eat.",
        address: "6235 Airport Wy S, Seattle, WA 98108",
        website: "https://www.deepseasugarandsalt.com",
        imageClass: "deep-sea-sugar-salt",
        backgroundImage: "images/dining-deep-sea-sugar-salt.jpg",
        coordinates: [-122.3148342, 47.5478544],
      }
    ]
  },
  {
    title: "Fremont & Ballard Eats",
    emoji: "🛶",
    restaurants: [
      {
        name: "Milstead & Co.",
        tags: ['coffee', 'espresso', 'pour-over'],
        emoji: "☕",
        description: "A must for coffee lovers. Milstead is one of Seattle's most respected cafés, pouring a rotating lineup of top roasters in a calm, minimal space near the Fremont Bridge.",
        address: "770 N 34th St, Seattle, WA 98103",
        website: "https://www.milsteadandco.com",
        imageClass: "milstead-co",
        backgroundImage: "images/dining-milstead-co.webp",
        coordinates: [-122.3483687, 47.6494434],
      },
      {
        name: "The Walrus and the Carpenter",
        tags: ['oysters', 'seafood', 'small plates', 'date night'],
        emoji: "🦪",
        description: "One of Seattle's most famous oyster bars, and it lives up to the hype: super-fresh local oysters and seasonal small plates in a bright, cheerful room in Ballard.",
        tip: "Go early or expect a wait.",
        address: "4743 Ballard Ave NW, Seattle, WA 98107",
        website: "https://www.thewalrusbar.com",
        imageClass: "walrus-carpenter",
        backgroundImage: "images/dining-oddfellows.webp",
        coordinates: [-122.3801366, 47.6636067],
      },
      {
        name: "Asadero Ballard",
        tags: ['steak', 'mexican', 'grill', 'meat'],
        emoji: "🥩",
        description: "Our pick for a big, meaty dinner: mesquite-grilled steaks with housemade tortillas and salsas in a dark, moody room.",
        address: "5405 Leary Ave NW, Seattle, WA 98107",
        website: "https://www.asaderoballard.com",
        imageClass: "asadero-ballard",
        backgroundImage: "images/dining-asadero.webp",
        coordinates: [-122.3826680, 47.6669218],
      },
      {
        name: "Miro Tea",
        tags: ['tea', 'bubble tea', 'boba', 'quiet'],
        emoji: "🍵",
        description: "A calm break from busy Ballard: a huge menu of loose-leaf teas, bubble teas, and light bites in a quiet, cozy room.",
        address: "5405 Leary Ave NW, Seattle, WA 98107",
        website: "https://www.mirotea.com",
        imageClass: "miro-tea",
        backgroundImage: "images/dining-miro-tea.webp",
        coordinates: [-122.3826680, 47.6669218],
      },
      {
        name: "Salt & Straw",
        tags: ['ice cream', 'dessert', 'kid-friendly'],
        emoji: "🍦",
        description: "Ice cream worth a detour. This Portland favorite makes wild, rotating seasonal flavors — sample a few before you choose.",
        address: "5420 Ballard Ave NW, Seattle, WA 98107",
        website: "https://www.saltandstraw.com",
        imageClass: "salt-straw",
        backgroundImage: "images/dining-salt-straw.webp",
        coordinates: [-122.3854746, 47.6682261],
      }
    ]
  },
  {
    title: "Pike Place / Waterfront",
    emoji: "🎣",
    restaurants: [
      {
        name: "The Alibi Room",
        tags: ['pizza', 'cocktails', 'post alley', 'gum wall', 'casual'],
        emoji: "🍕",
        description: "A cozy hideout steps from the Gum Wall. Tucked into Post Alley, it's our favorite low-key spot for pizza and a cocktail after a day at the market.",
        address: "85 Pike St #410, Seattle, WA 98101",
        website: "https://www.seattlealibi.com",
        imageClass: "alibi-room",
        backgroundImage: "images/dining-alibi-room.webp",
        coordinates: [-122.3403644, 47.6082567],
      },
      {
        name: "Place Pigalle",
        tags: ['french', 'bistro', 'views', 'date night', 'special occasion'],
        emoji: "🇫🇷",
        description: "Our pick for a special meal at the market. This intimate French bistro is tucked into Pike Place, with Northwest-inspired dishes, classic cocktails, and big views over Elliott Bay.",
        address: "81 Pike St, Seattle, WA 98101",
        website: "https://www.placepigalle-seattle.com",
        imageClass: "place-pigalle",
        backgroundImage: "images/dining-place-pigalle.webp",
        coordinates: [-122.3408538, 47.6083167],
      },
      {
        name: "Maximilien",
        tags: ['french', 'bistro', 'views', 'gluten', 'celiac', 'romantic', 'date night'],
        emoji: "🍷",
        description: "A romantic, old-world French bistro inside the market with sweeping water views — and plenty of gluten-free options.",
        address: "81A Pike St, Seattle, WA 98101",
        website: "https://www.maximilienrestaurant.com",
        imageClass: "maximilien",
        backgroundImage: "images/dining-maximilien-restaurant.webp",
        coordinates: [-122.3408538, 47.6083167],
      },
      {
        name: "The Nest",
        tags: ['rooftop', 'sunset', 'views', 'cocktails', 'bar'],
        emoji: "🍸",
        description: "Our favorite spot for a sunset drink. This rooftop bar atop the Thompson Hotel looks out over Elliott Bay and the Olympic Mountains.",
        address: "110 Stewart St, Seattle, WA 98101",
        website: "https://www.thompsonhotels.com/hotels/washington/seattle/thompson-seattle/eat-and-drink/the-nest",
        imageClass: "the-nest",
        backgroundImage: "images/dining-pike-place-waterfront.webp",
        coordinates: [-122.3413319, 47.6105512],
      },
      {
        name: "The Pink Door",
        tags: ['italian', 'pasta', 'cabaret', 'live music', 'date night'],
        emoji: "🇮🇹",
        description: "A Seattle institution. Find the unmarked pink door in Post Alley for Italian comfort food, a lively room, and, on some nights, live cabaret and trapeze.",
        address: "1919 Post Alley, Seattle, WA 98101",
        website: "https://www.thepinkdoor.net",
        imageClass: "pink-door",
        backgroundImage: "images/dining-pink-door-restaurant.webp",
        coordinates: [-122.3425312, 47.6103875],
      },
      {
        name: "Six Seven Restaurant",
        tags: ['seafood', 'waterfront', 'views', 'special occasion'],
        emoji: "🐟",
        description: "Waterfront dining at its best: seasonal menus built around local seafood, right on the water at the Edgewater Hotel, with views across Elliott Bay.",
        address: "2411 Alaskan Way Pier 67, Seattle, WA 98121",
        website: "https://www.edgewaterhotel.com/dining/",
        imageClass: "six-seven",
        backgroundImage: "images/dining-maximilien.webp",
        coordinates: [-122.3528448, 47.6125416],
      }
    ]
  },
  {
    title: "Downtown Dining",
    emoji: "🏙️",
    restaurants: [
      {
        name: "Din Tai Fung",
        tags: ['dumplings', 'xiaolongbao', 'chinese', 'groups', 'kid-friendly'],
        emoji: "🥟",
        description: "Worth the wait for the famous soup dumplings (xiaolongbao), folded by hand where you can watch through the kitchen window. Reliable, delicious, and great for groups.",
        address: "600 Pine St, Seattle, WA 98101",
        website: "https://dtf.com/en-us/locations/seattle-pacific-place",
        imageClass: "din-tai-fung",
        backgroundImage: "images/din-tai-fung.webp",
        coordinates: [-122.3352890, 47.6128833],
      },
      {
        name: "Serious Pie",
        tags: ['pizza', 'wood-fired'],
        emoji: "🍕",
        description: "Seattle chef Tom Douglas's pizza spot: wood-fired pies with blistered, chewy crusts and toppings like house-made sausage and seasonal vegetables. Perfect after a day downtown.",
        address: "316 Virginia St, Seattle, WA 98121",
        website: "https://www.seriouspieseattle.com/",
        imageClass: "serious-pie",
        backgroundImage: "images/serious-pie.webp",
        coordinates: [-122.3404945, 47.6129030],
      },
      {
        name: "Portage Bay Cafe",
        tags: ['breakfast', 'brunch', 'pancakes', 'french toast', 'kid-friendly'],
        emoji: "🥞",
        description: "Our favorite big breakfast: organic, locally sourced dishes and the famous toppings bar for your pancakes or French toast, piled with fresh fruit and whipped cream.",
        address: "391 Terry Ave N, Seattle, WA 98109",
        website: "https://www.portagebaycafe.com/",
        imageClass: "portage-bay-cafe",
        backgroundImage: "images/portage-bay-cafe.webp",
        coordinates: [-122.3374338, 47.6218203],
      },
      {
        name: "Daniel's Broiler",
        tags: ['steak', 'steakhouse', 'views', 'special occasion'],
        emoji: "🥩",
        description: "Our pick for a classic steakhouse splurge, with big windows over Lake Union and the seaplanes coming in.",
        address: "809 Fairview Pl N, Seattle, WA 98109",
        website: "https://www.schwartzbros.com/daniels-broiler/south-lake-union",
        imageClass: "daniels-broiler",
        backgroundImage: "images/dining-walrus-carpenter.webp",
        coordinates: [-122.3348787, 47.6267122],
      }
    ]
  }
];
